// Minimal native lifecycle bridge, not a settings UI or supported-device claim.
// No scoped secrets or coordinates are persisted in JS. Authenticated enrollment
// stays in the ordinary web account plane; native receives ONLY scoped authority.
function nativePlugin() {
  if (typeof window === 'undefined' || window.Capacitor?.isNativePlatform?.() !== true
    || window.Capacitor?.getPlatform?.() !== 'android') return null;
  return window.Capacitor.Plugins?.KataBackgroundPosition || null;
}

export class NativeBackgroundPositionBridge {
  constructor(plugin = nativePlugin) { this.plugin = plugin; this.scope = null; this.intent = 0; }
  bind(owner, login) {
    const native = this.plugin();
    if (!native) return Promise.resolve({ supported: false });
    if (this.scope?.owner === owner && this.scope?.login === login) return this.scope.ready;
    this.intent += 1;
    const scope = { owner, login, generation: null };
    this.scope = scope;
    // IPC dispatched now, before any authentication/storage/network awaits.
    scope.ready = Promise.resolve(native.bind({ owner, login })).then(result => {
      if (this.scope !== scope) throw new Error('Native owner changed');
      scope.generation = result.generation;
      return result;
    });
    return scope.ready;
  }
  observeLogin(login) {
    if (this.scope && this.scope.login !== login) { this.intent += 1; this.scope = null; }
    const native = this.plugin();
    return native ? Promise.resolve(native.observeLogin({ login })) : Promise.resolve();
  }
  detach() {
    this.intent += 1; this.scope = null;
    const native = this.plugin();
    return native ? Promise.resolve(native.detach()) : Promise.resolve();
  }
  stop() {
    this.intent += 1;
    const native = this.plugin();
    return native ? Promise.resolve(native.stop({ generation: this.scope?.generation ?? -1 })) : Promise.resolve();
  }
  async capabilities() {
    const native = this.plugin(), scope = this.scope;
    if (!native || !scope?.owner) return { native_collector_wired: false, device_qualified: false };
    await scope.ready;
    const result = await native.capabilities();
    if (scope !== this.scope) throw new Error('Native owner changed');
    if (typeof native.recover !== 'function' || result.retained_enrollment_recovery !== true) return { ...result, native_collector_wired: false, reason: 'upgrade_required' };
    return result;
  }
  async recover(scope, native, check) {
    const { install_id } = await native.installation(); check();
    const retained = await native.recover({ generation: scope.generation, install_id });
    if (scope !== this.scope) throw new Error('Native owner changed');
    if (!Number.isSafeInteger(retained.generation)) throw new Error('Native recovery incarnation unsupported');
    scope.generation = retained.generation; // Reattachment may have completed while local Stop invalidated intent.
    check();
    if (retained.authority_available === false) throw new Error('Original enrollment authority unavailable; retry explicitly. Pending rows retained and capture not started.');
    return { ...retained, install_id };
  }
  async startWithEnrollment(enroll) {
    const native = this.plugin(), scope = this.scope, intent = ++this.intent;
    if (!native || !scope?.owner) throw new Error('Native background capture unsupported or unbound');
    const current = () => this.scope === scope && this.intent === intent;
    const check = () => { if (!current()) throw new Error('Native owner or Start intent changed'); };
    await scope.ready; check();
    const capability = await native.capabilities(); check();
    if (capability.protocol_version !== 1 || capability.native_collector_wired !== true || capability.retained_enrollment_recovery !== true || typeof native.recover !== 'function') throw new Error('Native background capture unsupported; update Android app');
    if (typeof native.requestPermissions !== 'function') throw new Error('Native permissions unsupported; update Android app');
    const permission = await native.requestPermissions({ permissions: ['location', 'notifications'] }); check();
    if (permission.location !== 'granted' || permission.notifications !== 'granted') throw new Error('Location and visible Stop notification permission required');
    const retained = await this.recover(scope, native, check);
    if (retained.authorized !== true || retained.has_credential !== true) {
      if (retained.pending > 0 || retained.rejected > 0) throw new Error(retained.may_upload_pending === true
        ? 'Flush the retained original-enrollment backlog before replacing this tracker, then Start again.'
        : 'Original enrollment is revoked or unavailable; retained rows cannot be reassigned to a new credential. Capture remains stopped.');
      const enrollment = await enroll({ install_id: retained.install_id, platform: 'android', protocol_version: 1 }); check();
      const configured = await native.configure({ generation: scope.generation, install_id: retained.install_id, credential: enrollment.credential });
      if (scope !== this.scope || !Number.isSafeInteger(configured.generation)) throw new Error('Native incarnation changed or unsupported');
      scope.generation = configured.generation; check();
    }
    const result = await native.start({ generation: scope.generation }); check();
    return result;
  }
  async flush() {
    const native = this.plugin(), scope = this.scope;
    if (!native || !scope?.owner) throw new Error('Native background capture unsupported or unbound');
    const intent = this.intent;
    const check = () => { if (scope !== this.scope || intent !== this.intent) throw new Error('Native owner or flush intent changed'); };
    await scope.ready; check();
    if (typeof native.recover !== 'function') throw new Error('Original enrollment recovery unsupported; update Android app');
    const retained = await this.recover(scope, native, check);
    if (!retained.has_credential) {
      if (retained.pending > 0) throw new Error('Original enrollment unavailable; pending rows retained');
      return { state: 'stopped', pending: 0, rejected: retained.rejected };
    }
    if (!retained.authorized && !retained.may_upload_pending) throw new Error('Original enrollment revoked; pending rows retained, not reassigned');
    const result = await native.flush({ generation: scope.generation }); check();
    return result;
  }
  async status() {
    const native = this.plugin(), scope = this.scope;
    if (!native || !scope?.owner) return { state: 'unsupported' };
    await scope.ready;
    if (scope !== this.scope) throw new Error('Native owner changed');
    const result = await native.getStatus({ generation: scope.generation });
    if (scope !== this.scope) throw new Error('Native owner changed');
    return result;
  }
}

const nativeBackgroundPosition = new NativeBackgroundPositionBridge();
export default nativeBackgroundPosition;
